import {
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  ListObjectsV2CommandOutput,
  S3Client,
  _Object,
} from '@aws-sdk/client-s3';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DocumentIcon, ExclamationIcon, FolderIcon, PlusIcon, XIcon, SearchIcon, DownloadIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/solid';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { FileAdder, months } from './FileAdder';
import { LambdaClient } from '@aws-sdk/client-lambda';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';

interface DocumentDisplayerProps {
  s3Client: S3Client;
  lambdaClient: LambdaClient;
  isAdmin: boolean;
  isPostCollege: boolean;
  isAdvisor: boolean;
}

export const DocumentDisplayer = (props: DocumentDisplayerProps) => {
  const [documentMap, setDocumentMap] = useState(new Map<string, string[]>());
  const [arePreCollegeDocsLoaded, setArePreCollegeDocsLoaded] = useState(false);
  const [arePostCollegeDocsLoaded, setArePostCollegeDocsLoaded] = useState(false);
  const [restricedMap, setRestrictedMap] = useState(new Map<string, boolean>());
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showDeleteSpeedbump, setShowDeleteSpeedbump] = useState(false);
  const [fileToDelete, setFileToDelete] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [isBrowseOpen, setIsBrowseOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dragControls = useDragControls();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 200); // 200ms debounce
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!activeFolder) return;

    // Push a new entry to the history stack so "back" gesture works
    window.history.pushState({ folderOpen: true }, '');

    const handlePopState = () => {
      // Triggered by browser/system back gesture
      setActiveFolder(null);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // If the folder is closed manually (not via back button), 
      // remove the added history entry to keep history clean
      if (window.history.state?.folderOpen) {
        window.history.back();
      }
    };
  }, [activeFolder]);

  const cleanLabel = (label: string) => label.replace(/\s*meeting/gi, '').trim();

  const getFiles = async () => {
    const restricedMapStart = new Map<string, boolean>();
    const documentMapStart = new Map<string, string[]>();
    const addToMap = (objects: _Object[], restricted: boolean) => {
      objects.forEach((entry) => {
        if (!isNil(entry.Key) && !entry.Key.startsWith('posts/') && !isEmpty(entry.Key?.split('/')[1])) {
          const splitString = entry.Key?.split('/');
          const children = documentMapStart.get(splitString[0]);
          if (isNil(children)) {
            documentMapStart.set(splitString[0], [entry.Key]);
          } else {
            children.push(entry.Key);
            documentMapStart.set(splitString[0], children);
          }
          restricedMapStart.set(entry.Key, restricted);
        }
      });
    };

    const fetchBucket = async (bucket: string, restricted: boolean) => {
      let isTruncated = true;
      let continuationToken: string | undefined = undefined;
      while (isTruncated) {
        const output: ListObjectsV2CommandOutput = await props.s3Client.send(
          new ListObjectsV2Command({
            Bucket: bucket,
            ContinuationToken: continuationToken,
          })
        );
        if (!isNil(output.Contents)) {
          addToMap(output.Contents, restricted);
        }
        isTruncated = output.IsTruncated ?? false;
        continuationToken = output.NextContinuationToken;
      }
    };

    try {
      await fetchBucket('protected-mitchellfc-docs-everyone', false);
      if (props.isPostCollege || props.isAdvisor) {
        await fetchBucket('protected-mitchellfc-docs-post-grad', true);
      }
      setDocumentMap(new Map(documentMapStart));
      setRestrictedMap(new Map(restricedMapStart));
      setArePreCollegeDocsLoaded(true);
      setArePostCollegeDocsLoaded(true);
    } catch (e) {
      console.error("Failed to load documents:", e);
    }
  };

  useEffect(() => {
    if (!isNil(props.lambdaClient) && !isNil(props.s3Client)) {
      getFiles();
    }
  }, [props.lambdaClient, props.s3Client]);

  useEffect(() => {
    const isLocked = showAddFileModal || showDeleteSpeedbump || activeFolder;
    if (isLocked) {
      const scrollY = window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.setAttribute('data-scroll-pos', scrollY.toString());
    } else {
      const scrollY = document.body.getAttribute('data-scroll-pos');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
        document.body.removeAttribute('data-scroll-pos');
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [showAddFileModal, showDeleteSpeedbump, activeFolder]);

  const deleteFile = (fileName?: string) => {
    const file = isNil(fileName) ? fileToDelete : fileName;
    setIsPageLoading(true);
    const bucket = restricedMap.get(file) ? 'protected-mitchellfc-docs-post-grad' : 'protected-mitchellfc-docs-everyone';
    props.s3Client
      .send(new DeleteObjectCommand({ Bucket: bucket, Key: file }))
      .then(() => {
        setIsPageLoading(false);
        setFileToDelete('');
        setShowDeleteSpeedbump(false);
        getFiles();
      })
      .catch((e) => console.error(e));
  };

  const viewFile = async (fileName: string) => {
    const bucket = restricedMap.get(fileName) ? 'protected-mitchellfc-docs-post-grad' : 'protected-mitchellfc-docs-everyone';
    const getObjectCommand = new GetObjectCommand({ Bucket: bucket, Key: fileName });
    
    // Pre-open the window to satisfy mobile popup blockers
    const newWindow = window.open('about:blank', '_blank');

    try {
      const url = await getSignedUrl(props.s3Client, getObjectCommand, { expiresIn: 3600 });
      if (newWindow) {
        newWindow.location.href = url;
      }
    } catch (e) {
      if (newWindow) newWindow.close();
      console.error('Error occured', e);
    }
  };

  const downloadFile = async (fileName: string) => {
    const bucket = restricedMap.get(fileName) ? 'protected-mitchellfc-docs-post-grad' : 'protected-mitchellfc-docs-everyone';
    const getObjectCommand = new GetObjectCommand({ 
      Bucket: bucket, 
      Key: fileName,
      ResponseContentDisposition: `attachment; filename="${fileName.split('/').pop()}"`
    });
    try {
      const url = await getSignedUrl(props.s3Client, getObjectCommand, { expiresIn: 3600 });
      if (isMobile) {
        // location.assign with attachment header is the best way to trigger a download on mobile
        window.location.assign(url);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName.split('/').pop() || 'file');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    } catch (e) {
      console.error('Error occured', e);
    }
  };

  const folderData = useMemo(() => {
    const keys = Array.from(documentMap.keys());
    
    keys.sort((a, b) => {
      const labelA = cleanLabel(a);
      const labelB = cleanLabel(b);
      
      // 1. "General" always at the very top
      if (labelA.toLowerCase() === 'general') return -1;
      if (labelB.toLowerCase() === 'general') return 1;

      const partsA = labelA.split(' ');
      const partsB = labelB.split(' ');
      
      const yearA = parseInt(partsA[partsA.length - 1]);
      const yearB = parseInt(partsB[partsB.length - 1]);

      // 2. Sort by Year (Descending: 2026, 2025...)
      if (!isNaN(yearA) && !isNaN(yearB) && yearA !== yearB) {
        return yearB - yearA;
      }

      // 3. Sort by Month (Ascending: Jan -> Dec) within that year
      return months.indexOf(partsA[0]) - months.indexOf(partsB[0]);
    });

    return keys.map(key => ({
      key,
      label: cleanLabel(key),
      count: documentMap.get(key)?.length || 0
    }));
  }, [documentMap]);

  const areAllDocumentsLoaded = () => arePreCollegeDocsLoaded && arePostCollegeDocsLoaded;

  const searchResults = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return [];
    
    const results: { key: string; folder: string }[] = [];
    documentMap.forEach((docs, folder) => {
      docs.forEach(doc => {
        if (doc.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) || folder.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) {
          results.push({ key: doc, folder });
        }
      });
    });

    return results.sort((a, b) => a.key.localeCompare(b.key));
  }, [documentMap, debouncedSearchQuery]);

  const renderSearchResults = () => {
    if (searchResults.length === 0) {
      return (
        <div className="py-20 text-center">
          <p className="text-slate-500 font-bold text-lg">No documents found matching "{debouncedSearchQuery}"</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-sm font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Discovery Results</h2>
          <div className="h-px flex-grow bg-slate-100 dark:bg-white/5" />
          <span className="text-xs font-bold text-slate-400">{searchResults.length} Matches</span>
        </div>
        {searchResults.map(({ key, folder }) => (
          <div 
            key={key} 
            className="group flex items-center justify-between p-4 bg-white dark:bg-slate-800/40 sm:backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/5 sm:hover:shadow-xl sm:hover:border-blue-500/30 transition-all duration-300"
          >
            <div onClick={() => viewFile(key)} className="flex items-center gap-4 cursor-pointer overflow-hidden flex-grow mr-4">
              <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                <DocumentIcon className="h-5 w-5" />
              </div>
              <div className="overflow-hidden">
                <span className="font-bold text-slate-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate block">
                  {key.split('/')[1].split('.')[0]}
                </span>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mt-0.5">
                  <FolderIcon className="h-3 w-3" /> {cleanLabel(folder)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {props.isAdmin && (
                <button 
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFileToDelete(key); setShowDeleteSpeedbump(true); }}
                  className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              )}
              <button 
                type="button"
                onClick={(e) => { e.stopPropagation(); downloadFile(key); }} 
                className="p-2.5 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-500/20 sm:active:scale-90 transition-all"
              >
                <DownloadIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderFolderList = () => {
    return (
      <div className="space-y-4">
        <button 
          type="button"
          onClick={() => setIsBrowseOpen(!isBrowseOpen)}
          className={`w-full flex items-center justify-between p-5 rounded-[2.5rem] border group ${
            isBrowseOpen 
              ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-500/20' 
              : 'bg-white dark:bg-slate-800/40 border-slate-200 dark:border-white/5 text-slate-900 dark:text-white hover:border-blue-500/50 hover:shadow-lg sm:hover:-translate-y-1 hover:bg-slate-50 dark:hover:bg-white/5'
          } shadow-xl shadow-slate-200/50 dark:shadow-none transition-colors duration-200`}
        >
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${isBrowseOpen ? 'bg-white/20' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'}`}>
              <FolderIcon className="h-6 w-6" />
            </div>
            <div className="text-left">
              <span className="block font-black uppercase tracking-widest text-sm">Browse Folders</span>
              <span className={`text-[10px] font-bold uppercase tracking-widest mt-0.5 ${isBrowseOpen ? 'text-white/60' : 'text-slate-400'}`}>
                {folderData.length} Total Folders
              </span>
            </div>
          </div>
          {isBrowseOpen ? <ChevronDownIcon className="h-6 w-6" /> : <ChevronRightIcon className="h-6 w-6 opacity-40" />}
        </button>

        <AnimatePresence>
          {isBrowseOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "circOut" }}
              className="overflow-hidden"
            >
              <div className="pt-2 pl-4 sm:pl-8 space-y-2">
                {folderData.map((folder) => (
                  <motion.div 
                    key={folder.key}
                    onClick={() => setActiveFolder(folder.key)}
                    className="flex items-center justify-between p-5 cursor-pointer bg-slate-100/50 dark:bg-white/5 hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-[2.5rem] border border-slate-200/50 dark:border-white/10 hover:border-blue-100 dark:hover:border-blue-900/20 transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-blue-500 transition-colors">
                        <FolderIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <span className="text-base font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {folder.label}
                        </span>
                        <span className="text-[10px] block font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                          {folder.count} Files
                        </span>
                      </div>
                    </div>
                    <ChevronRightIcon className="h-6 w-6 text-slate-300 group-hover:text-blue-400 transition-colors" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
  const folderModal = (
    <AnimatePresence>
      {activeFolder && (
        <div className={`fixed inset-0 z-[600] flex ${isMobile ? 'items-end' : 'justify-end'}`}>
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-md touch-none"
            onClick={() => setActiveFolder(null)}
          />
          <motion.div 
            initial={isMobile ? { y: '100%' } : { x: '100%' }} 
            animate={isMobile ? { y: 0 } : { x: 0 }} 
            exit={isMobile ? { y: '100%' } : { x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            drag={isMobile ? "y" : false}
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0 }}
            dragElastic={0.1}
            onDragEnd={(_, info) => {
              if (isMobile && (info.offset.y > 150 || info.velocity.y > 500)) {
                setActiveFolder(null);
              }
            }}
            className={`relative bg-white dark:bg-slate-900 shadow-2xl flex flex-col overflow-hidden ${
              isMobile 
                ? 'w-full h-[85vh] rounded-t-[2.5rem] border-t border-slate-200 dark:border-white/10' 
                : 'w-full max-w-xl h-full border-l border-slate-200 dark:border-white/10'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {isMobile && (
              <div 
                onPointerDown={(e) => dragControls.start(e)}
                className="w-full pt-4 pb-1 cursor-grab active:cursor-grabbing touch-none shrink-0"
              >
                <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto" />
              </div>
            )}
            <div 
              onPointerDown={(e) => isMobile && dragControls.start(e)}
              className="p-6 sm:p-8 border-b dark:border-white/5 flex items-center justify-between shrink-0"
            >
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <FolderIcon className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">{cleanLabel(activeFolder)}</h3>
                  <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Folder Archives</p>
                </div>
              </div>
              {!isMobile && (
                <button 
                  onClick={() => setActiveFolder(null)} 
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-2 bg-slate-200/50 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 rounded-full text-slate-600 dark:text-slate-400 transition-all active:scale-90"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              )}
            </div>
            
            <div className="flex-grow overflow-y-auto overscroll-contain p-4 sm:p-8 space-y-3">
              {documentMap.get(activeFolder)?.sort().map((doc) => (
                <div key={doc} className="group flex items-center justify-between p-4 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/5 hover:bg-white dark:hover:bg-white/10 hover:shadow-xl hover:border-blue-500/20 transition-all duration-300">
                  <div onClick={() => viewFile(doc)} className="flex items-center gap-4 cursor-pointer overflow-hidden flex-grow mr-4">
                    <DocumentIcon className="h-6 w-6 text-slate-400 group-hover:text-blue-500 transition-colors shrink-0" />
                    <span className="font-bold text-slate-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">{doc.split('/')[1].split('.')[0]}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {props.isAdmin && (
                      <button 
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFileToDelete(doc); setShowDeleteSpeedbump(true); }}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                      >
                        <XIcon className="h-5 w-5" />
                      </button>
                    )}
                    <button 
                      type="button"
                      onClick={(e) => { e.stopPropagation(); downloadFile(doc); }} 
                      className="p-2.5 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-500/20 active:scale-90 transition-all"
                    >
                      <DownloadIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  const deleteSpeedbump = (
    <AnimatePresence>
      {showDeleteSpeedbump && (
        <div className="fixed inset-0 z-[700] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-950/90 backdrop-blur-md" onClick={() => setShowDeleteSpeedbump(false)} />
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl max-w-md w-full border dark:border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="h-14 w-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4 text-red-600 dark:text-red-400">
                <ExclamationIcon className="h-7 w-7" />
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Delete File?</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed text-center">Are you sure you want to remove <span className="font-bold text-slate-900 dark:text-white">{fileToDelete.split('/')[1]}</span>? This cannot be undone.</p>
              <div className="flex gap-4">
                <button type="button" onClick={() => deleteFile()} className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-red-700 transition-all shadow-lg active:scale-95">Delete</button>
                <button type="button" onClick={() => setShowDeleteSpeedbump(false)} className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95">Cancel</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  const addFileModal = (
    <AnimatePresence>
      {showAddFileModal && (
        <div className="fixed inset-0 z-[800] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" onClick={() => setShowAddFileModal(false)} />
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[2.5rem] overflow-hidden shadow-2xl border dark:border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-black tracking-tight dark:text-white text-slate-900 uppercase tracking-widest">Upload Documents</h3>
                <button type="button" onClick={() => setShowAddFileModal(false)} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <FileAdder lambdaClient={props.lambdaClient as LambdaClient} getFiles={async () => { await getFiles(); setShowAddFileModal(false); }} s3Client={props.s3Client as S3Client} />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="w-full">
      {isPageLoading && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-[900] flex items-center justify-center">
          <div className="h-12 w-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* PORTALS: Physically render modals at the body level to escape transformed parents */}
      {createPortal(folderModal, document.body)}
      {createPortal(deleteSpeedbump, document.body)}
      {createPortal(addFileModal, document.body)}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-12">
        <div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tighter dark:text-white text-slate-900 uppercase">Documents</h1>
          <p className="text-slate-500 dark:text-slate-400 font-bold text-sm uppercase tracking-widest mt-1">Official Family Archives</p>
        </div>
        {props.isAdmin && (
          <button type="button" onClick={() => setShowAddFileModal(true)} className="inline-flex items-center justify-center rounded-xl px-6 py-3 bg-blue-600 text-sm font-black text-white hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-500/20 active:scale-95">
            <PlusIcon className="h-5 w-5 mr-2" /> Upload New
          </button>
        )}
      </div>

      <div className="mb-8 relative group">
        <input
          type="text"
          placeholder="Search Archives..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full p-4 pl-12 rounded-2xl border border-slate-200 dark:border-white/5 bg-white dark:bg-slate-800/40 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-xl placeholder:text-slate-400 font-bold"
        />
        <SearchIcon className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
      </div>

      {!areAllDocumentsLoaded() ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 animate-pulse">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="h-40 bg-slate-100 dark:bg-slate-800/40 rounded-[2rem]" />)}
        </div>
      ) : (
        <div className="relative">
          {debouncedSearchQuery.trim() ? (
            <div key="search-results">
              {renderSearchResults()}
            </div>
          ) : (
            <div key="folder-list">
              {renderFolderList()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
